import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Spinner, Table, Image, Modal } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function ProductDetail() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    const initialProduct = location.state?.product || null;
    const [product, setProduct] = useState(initialProduct);
    const [loading, setLoading] = useState(!initialProduct);
    const [showFullDesc, setShowFullDesc] = useState(false);
    const [modalImage, setModalImage] = useState(null);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    useEffect(() => {
        if (!initialProduct) {
            const fetchProductDetail = async () => {
                try {
                    const res = await axios.get(
                        `${process.env.REACT_APP_API_URL}/admin/products/detail`,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                            },
                        }
                    );
                    setProduct(res.data?.data || res.data);
                } catch (err) {
                    console.error("Failed to fetch product details:", err);
                } finally {
                    setLoading(false);
                }
            };
            fetchProductDetail();
        }
    }, [initialProduct]);

    if (loading || !product) {
        return (
            <PageLayout>
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted fw-semibold">{t("Loading product detail...")}</p>
                </div>
            </PageLayout>
        );
    }

    let buyerDetails = null;
    if (product.order?.order?.buyer_details) {
        try {
            buyerDetails = typeof product.order.order.buyer_details === "string"
                ? JSON.parse(product.order.order.buyer_details)
                : product.order.order.buyer_details;
        } catch (e) {
            buyerDetails = null;
        }
    }

    const orderData = product.order?.order;
    const orderDetail = product.order?.order_detail;

    const decodeHtmlEntities = (htmlStr) => {
        if (!htmlStr) return "";
        const textarea = document.createElement("textarea");
        textarea.innerHTML = htmlStr;
        return textarea.value;
    };

    const timelineTime = (value) => {
        const match = String(value || "").match(
            /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4}),?\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
        );

        if (!match) return 0;

        const months = {
            jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
        };

        const [, day, month, year, hour, minute, period] = match;
        const hour24 = (Number(hour) % 12) + (period.toUpperCase() === "PM" ? 12 : 0);

        return new Date(
            Number(year),
            months[month.toLowerCase()],
            Number(day),
            hour24,
            Number(minute)
        ).getTime();
    };

    return (
        <PageLayout>
            <style>{`
                .description-card {
                    background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
                    border: 1px solid #e9ecf1;
                    border-radius: 14px;
                    transition: max-height 0.35s ease;
                    overflow: hidden;
                }
                .product-description {
                    font-size: 0.925rem;
                    line-height: 1.8;
                    color: #4b5563;
                }
                .product-description p { margin-bottom: 0.9rem; }
                .product-description p:last-child { margin-bottom: 0; }
                .product-description h1,
                .product-description h2,
                .product-description h3,
                .product-description h4 {
                    color: #111827;
                    font-weight: 700;
                    font-size: 1rem;
                    margin: 1.1rem 0 0.5rem;
                }
                .product-description h1:first-child,
                .product-description h2:first-child,
                .product-description h3:first-child,
                .product-description h4:first-child { margin-top: 0; }
                .product-description ul,
                .product-description ol { margin-bottom: 0.9rem; padding-left: 1.3rem; }
                .product-description li { margin-bottom: 0.4rem; }
                .product-description li::marker { color: #2563eb; }
                .product-description strong,
                .product-description b { color: #111827; font-weight: 600; }
                .product-description a { color: #2563eb; text-decoration: underline; }
                
                .product-description table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                    margin: 1rem 0 !important;
                }
                .product-description table th,
                .product-description table td {
                    border: 1px solid #dee2e6 !important;
                    padding: 8px 12px !important;
                    text-align: left !important;
                }
                .product-description table th {
                    background-color: #f8f9fa !important;
                    font-weight: 600 !important;
                }

                .desc-fade-overlay {
                    position: absolute;
                    bottom: 0; left: 0; right: 0;
                    height: 64px;
                    background: linear-gradient(180deg, rgba(250,251,252,0) 0%, #fafbfc 85%);
                    border-radius: 0 0 14px 14px;
                    pointer-events: none;
                }
                .desc-toggle-btn {
                    border: none;
                    background: none;
                    color: #2563eb;
                    font-weight: 600;
                    font-size: 0.85rem;
                    padding: 0.4rem 0 0;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                .desc-toggle-btn:hover { color: #1d4ed8; }
            `}</style>

            <Row className="g-4">
                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body className="p-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                <div className="text-start">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <h4 className="mb-0 fw-bold text-dark">{product.title}</h4>
                                        <span className="badge bg-light text-secondary border font-monospace fs-6">
                                            #{product.product_code}
                                        </span>
                                    </div>
                                    <nav aria-label="breadcrumb">
                                        <ol className="breadcrumb mb-0 small">
                                            <li className="breadcrumb-item">
                                                <Link to="#" className="text-decoration-none text-muted">{t("Home")}</Link>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <Link to="/products" className="text-decoration-none text-muted">{t("Products")}</Link>
                                            </li>
                                            <li className="breadcrumb-item active text-primary fw-semibold" aria-current="page">
                                                {t("Detail")}
                                            </li>
                                        </ol>
                                    </nav>
                                </div>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="px-3 py-2 rounded-pill d-flex align-items-center gap-2 border shadow-sm text-secondary fw-semibold"
                                    onClick={() => navigate(-1)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5" />
                                        <path d="M12 19l-7-7 7-7" />
                                    </svg>
                                    <span>{t("Back") || "Back"}</span>
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={8}>
                    <Card className="mb-4 border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h6 className="mb-0 fw-bold text-secondary">{t("Product Overview")}</h6>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="light" className="text-dark border fw-normal px-2 py-1">
                                    {t("Condition")}: <strong>{product.product_condition}</strong>
                                </Badge>
                                <Badge bg="light" className="text-dark border fw-normal px-2 py-1">
                                    {t("Style")}: <strong>{product.selling_style}</strong>
                                </Badge>
                                <Badge bg={product.sold_on ? "success" : "warning"} className="px-2 py-1">
                                    {product.sold_on ? t("Sold") : product.status}
                                </Badge>
                            </div>
                        </Card.Header>

                        <Card.Body className="p-4 text-start">
                            <Row className="g-4 mb-4">
                                <Col md={5}>
                                    <div className="mb-3">
                                        <span className="d-block text-muted small fw-semibold mb-2">{t("Primary Image")}</span>
                                        {product.img?.first_img ? (
                                            <div className="p-1 border rounded-3 bg-light cursor-pointer text-center" onClick={() => setModalImage(`${imgBaseUrl}${product.img.first_img}`)}>
                                                <Image
                                                    src={`${imgBaseUrl}${product.img.first_img}`}
                                                    alt="Primary Product"
                                                    fluid
                                                    rounded
                                                    style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="p-3 border rounded-3 bg-light text-muted small">{t("No Image")}</div>
                                        )}
                                    </div>

                                    {product.img?.card_img && (
                                        <div>
                                            <span className="d-block text-muted small fw-semibold mb-2">{t("Card Preview")}</span>
                                            <div className="p-1 border rounded-3 bg-light d-inline-block cursor-pointer" onClick={() => setModalImage(`${imgBaseUrl}${product.img.card_img}`)}>
                                                <Image
                                                    src={`${imgBaseUrl}${product.img.card_img}`}
                                                    alt="Card Preview"
                                                    fluid
                                                    rounded
                                                    style={{ width: "70px", height: "70px", objectFit: "contain" }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </Col>

                                <Col md={7}>
                                    <div className="mb-3">
                                        <h6 className="fw-bold text-dark mb-2">
                                            {t("Short Description")}
                                        </h6>

                                        {product.short_description ? (
                                            <div
                                                className="product-description p-3 bg-light border rounded-3 text-secondary small mb-0"
                                                dangerouslySetInnerHTML={{
                                                    __html: decodeHtmlEntities(product.short_description),
                                                }}
                                            />
                                        ) : (
                                            <p className="text-secondary small mb-0">N/A</p>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <div className="p-3 bg-light rounded-3 border mb-4">
                                <Row className="g-3 text-center">
                                    <Col xs={6} sm={3} className="border-end">
                                        <span className="text-muted d-block small">{t("Quantity")}</span>
                                        <strong className="text-dark fs-6">{product.qty}</strong>
                                    </Col>
                                    <Col xs={6} sm={3} className="border-end">
                                        <span className="text-muted d-block small">{t("Brand")}</span>
                                        <strong className="text-dark">{product.brand?.name || "N/A"}</strong>
                                    </Col>
                                    <Col xs={6} sm={3} className="border-end">
                                        <span className="text-muted d-block small">{t("Category")}</span>
                                        <strong className="text-dark">{product.category?.name || "N/A"}</strong>
                                    </Col>
                                    <Col xs={6} sm={3}>
                                        <span className="text-muted d-block small">{t("Store")}</span>
                                        <strong className="text-dark">{product.store?.name || "N/A"}</strong>
                                    </Col>
                                </Row>
                            </div>

                            <div className="mb-4">
                                <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 6h16M4 12h16M4 18h10" />
                                    </svg>
                                    {t("Full Description")}
                                </h6>

                                <div className="position-relative">
                                    <div
                                        className="description-card product-description p-4"
                                        style={{
                                            maxHeight: showFullDesc ? "1000px" : "170px",
                                            overflow: "hidden",
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: decodeHtmlEntities(product.description) || "N/A"
                                        }}
                                    />
                                    {!showFullDesc && <div className="desc-fade-overlay" />}
                                </div>

                                <button
                                    type="button"
                                    className="desc-toggle-btn"
                                    onClick={() => setShowFullDesc((v) => !v)}
                                >
                                    {showFullDesc ? t("Show less") : t("Show more")}
                                    <svg
                                        width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                        style={{ transform: showFullDesc ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
                                    >
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </button>
                            </div>

                            {Array.isArray(product.images) && product.images.length > 0 && (
                                <div className="mb-4">
                                    <h6 className="fw-bold text-dark mb-2">{t("Gallery Images")}</h6>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {product.images.map((imgObj, idx) => (
                                            <div key={idx} className="border rounded-3 p-1 bg-light cursor-pointer" onClick={() => setModalImage(`${imgBaseUrl}${imgObj.image}`)}>
                                                <Image
                                                    src={`${imgBaseUrl}${imgObj.image}`}
                                                    alt={`Gallery image ${idx + 1}`}
                                                    rounded
                                                    style={{ width: "65px", height: "65px", objectFit: "cover" }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mb-4">
                                <h6 className="fw-bold text-dark mb-2">{t("Pricing Details")}</h6>
                                <Row className="g-3">
                                    <Col sm={4}>
                                        <div className="p-3 border rounded-3 bg-light text-center">
                                            <span className="text-muted d-block small">{t("Buying Price PKR")}</span>
                                            <h5 className="mb-0 text-dark fw-bold">{product.pricing?.buying_price ?? "0.00"}</h5>
                                        </div>
                                    </Col>
                                    <Col sm={4}>
                                        <div className="p-3 border rounded-3 bg-light text-center">
                                            <span className="text-muted d-block small">{t("Sell Price ($)")}</span>
                                            <h5 className="mb-0 text-primary fw-bold">${product.pricing?.sell_price_dollar ?? "0.00"}</h5>
                                        </div>
                                    </Col>
                                    {product.selling_style === "Auction" && (
                                        <Col sm={4}>
                                            <div className="p-3 border rounded-3 bg-light text-center">
                                                <span className="text-muted d-block small">{t("Auction Min Price ($)")}</span>
                                                <h5 className="mb-0 text-primary fw-bold">${product.pricing?.auction_min_price ?? product.auction_min_price ?? "0.00"}</h5>
                                            </div>
                                        </Col>
                                    )}
                                    <Col sm={4}>
                                        <div className="p-3 border rounded-3 bg-light text-center">
                                            <span className="text-muted d-block small">{t("Shipping Charges")}</span>
                                            <h5 className="mb-0 text-dark fw-bold">${product.pricing?.shipping_charges_dollar ?? "0.00"}</h5>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            <div>
                                <h6 className="fw-bold text-dark mb-2">{t("Internal System Users")}</h6>
                                <div className="p-3 bg-light rounded-3 border">
                                    <Row className="g-2 text-muted small text-center">
                                        <Col sm={4}>{t("Issuer")}: <strong className="text-dark">{product.issuer?.name || "N/A"}</strong></Col>
                                        <Col sm={4}>{t("Lister")}: <strong className="text-dark">{product.lister?.name || "N/A"}</strong></Col>
                                        <Col sm={4}>{t("Market Placer")}: <strong className="text-dark">{product.market_placer?.name || "N/A"}</strong></Col>
                                    </Row>
                                </div>
                            </div>
                        </Card.Body>


                    </Card>

                    {orderData && (
                        <Card className="mb-4 border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="bg-white py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-secondary">{t("Order & Buyer Details")}</h6>
                            </Card.Header>

                            <Card.Body className="p-4 text-start">
                                <Row className="g-4 mb-4">
                                    <Col md={6}>
                                        <h6 className="fw-bold text-dark mb-2">{t("Order Master Info")}</h6>
                                        <div className="border rounded-3 p-2 bg-light">
                                            <Table size="sm" borderless responsive className="mb-0 small align-middle">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-muted">{t("Order Number")}</td>
                                                        <td className="fw-semibold text-dark text-end">{orderData.order_number}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Payment Status")}</td>
                                                        <td className="text-end"><Badge bg="warning" className="text-dark">{orderData.payment_status}</Badge></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Delivery Status")}</td>
                                                        <td className="text-end"><Badge bg="info">{orderData.delivery_status}</Badge></td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Tracking Number")}</td>
                                                        <td className="fw-semibold text-dark text-end">{orderData.tracking_number || "N/A"}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Courier")}</td>
                                                        <td className="fw-semibold text-dark text-end">{orderData.courier_company?.name || "N/A"}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Delivery Days")}</td>
                                                        <td className="fw-semibold text-dark text-end">{orderData.delivery_days} {t("Days")}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Payment Date")}</td>
                                                        <td className="fw-semibold text-dark text-end">
                                                            {orderData.payment_date ? new Date(orderData.payment_date).toLocaleString() : "N/A"}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <h6 className="fw-bold text-dark mb-2">{t("Financial Breakdown")}</h6>
                                        <div className="border rounded-3 p-2 bg-light">
                                            <Table size="sm" borderless responsive className="mb-0 small align-middle">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-muted">{t("Subtotal")}</td>
                                                        <td className="fw-semibold text-dark text-end">${orderData.subtotal}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Shipping Charges")}</td>
                                                        <td className="fw-semibold text-dark text-end">${orderData.shipping_charges}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Grand Total")}</td>
                                                        <td className="fw-bold text-success text-end">${orderData.grand_total}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Ordered At")}</td>
                                                        <td className="fw-semibold text-dark text-end">{new Date(orderData.ordered_at).toLocaleString()}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Processing At")}</td>
                                                        <td className="fw-semibold text-dark text-end">{new Date(orderData.processing_at).toLocaleString()}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">{t("Posted At")}</td>
                                                        <td className="fw-semibold text-dark text-end">{new Date(orderData.posted_at).toLocaleString()}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Col>
                                </Row>

                                <h6 className="fw-bold text-dark mb-2">{t("Buyer Profile & Shipping Address")}</h6>
                                <Row className="g-3 mb-3">
                                    <Col md={6}>
                                        <div className="p-3 border rounded-3 bg-light small h-100">
                                            <span className="text-primary fw-bold d-block border-bottom pb-1 mb-2">{t("Ordered By User")}</span>
                                            <div className="mb-1">{t("Name")}: <strong className="text-dark">{orderData.ordered_by?.name || "N/A"}</strong></div>
                                            <div className="mb-1">{t("Email")}: <strong className="text-dark">{orderData.ordered_by?.email || "N/A"}</strong></div>
                                            <div>{t("Role")}: <strong className="text-dark">{orderData.ordered_by?.role || "N/A"}</strong></div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="p-3 border rounded-3 bg-light small h-100">
                                            <span className="text-primary fw-bold d-block border-bottom pb-1 mb-2">{t("Buyer System Info")}</span>
                                            <div className="mb-1">{t("Buyer Name")}: <strong className="text-dark">{orderData.buyer?.buyer_name || "N/A"}</strong></div>
                                            <div className="mb-1">{t("Phone")}: <strong className="text-dark">{orderData.buyer?.buyer_phone || "N/A"}</strong></div>
                                            <div className="mb-1">{t("Address")}: <strong className="text-dark">{orderData.buyer?.buyer_address || "N/A"}</strong></div>
                                            <div>{t("VAT ID")}: <strong className="text-dark">{orderData.buyer?.vat_id || "N/A"}</strong></div>
                                        </div>
                                    </Col>
                                </Row>

                                {buyerDetails && (
                                    <div className="p-3 border rounded-3 bg-light mb-3 small">
                                        <span className="text-primary fw-bold d-block border-bottom pb-1 mb-2">{t("Parsed Buyer Details")}</span>
                                        <Row className="g-2">
                                            <Col sm={4}>{t("Name")}: <strong className="text-dark">{buyerDetails.name || "N/A"}</strong></Col>
                                            <Col sm={4}>{t("Email")}: <strong className="text-dark">{buyerDetails.email || "N/A"}</strong></Col>
                                            <Col sm={4}>{t("Phone")}: <strong className="text-dark">{buyerDetails.phone || "N/A"}</strong></Col>
                                            <Col sm={4}>{t("City")}: <strong className="text-dark">{buyerDetails.city || "N/A"}</strong></Col>
                                            <Col sm={4}>{t("Country")}: <strong className="text-dark">{buyerDetails.country || "N/A"}</strong></Col>
                                            <Col sm={4}>{t("VAT ID")}: <strong className="text-dark">{buyerDetails.vat_id || "N/A"}</strong></Col>
                                            <Col sm={12}>{t("Address")}: <strong className="text-dark">{buyerDetails.address || "N/A"}</strong></Col>
                                        </Row>
                                    </div>
                                )}

                                {orderDetail && (
                                    <div className="mt-4">
                                        <h6 className="fw-bold text-dark mb-2">{t("Item Purchased Detail")}</h6>
                                        <Table size="sm" bordered responsive className="small align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>{t("Description")}</th>
                                                    <th className="text-center">{t("Quantity")}</th>
                                                    <th className="text-end">{t("Sell_price_pkr")}</th>
                                                    <th className="text-end">{t("Shipping")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{orderDetail.description}</td>
                                                    <td className="text-center">{orderDetail.quantity}</td>
                                                    <td className="text-end">{orderDetail.Sell_price_pkr ?? '-'}</td>
                                                    <td className="text-end">${orderDetail.shipping ?? '-'}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {orderData.marketplaces?.map((item) => {
                                    const marketplaceUrl =
                                        item.type === "id"
                                            ? `${item.marketplace?.base_url || ""}${item.reference || ""}`
                                            : item.reference;

                                    return (
                                        <div
                                            key={item.id}
                                            className="d-flex align-items-center gap-2 mt-3 p-3 border rounded-3 bg-light"
                                        >
                                            {item.marketplace?.logo && (
                                                <Image
                                                    src={`${imgBaseUrl}${item.marketplace.logo}`}
                                                    alt={`${item.marketplace.name || "Marketplace"} Logo`}
                                                    className="cursor-pointer"
                                                    style={{
                                                        width: "24px",
                                                        height: "24px",
                                                        objectFit: "contain",
                                                    }}
                                                    onClick={() =>
                                                        setModalImage(
                                                            `${imgBaseUrl}${item.marketplace.logo}`
                                                        )
                                                    }
                                                />
                                            )}

                                            <span className="small text-muted">
                                                {t("Marketplace")}:{" "}
                                                <strong className="text-dark">
                                                    {item.marketplace?.name}
                                                </strong>
                                                {" - "}
                                                <a
                                                    href={marketplaceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-decoration-none fw-semibold"
                                                >
                                                    {marketplaceUrl}
                                                </a>
                                            </span>
                                        </div>
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col xl={4}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="bg-white py-3 border-bottom">
                            <h6 className="mb-0 fw-bold text-secondary">{t("Product Movement Timeline")}</h6>
                        </Card.Header>
                        <Card.Body className="p-4 text-start">
                            {Array.isArray(product.timeline) && product.timeline.length > 0 ? (
                                <div className="timeline-container position-relative ps-2">
                                    <div
                                        className="position-absolute bg-primary bg-opacity-25"
                                        style={{
                                            left: "11px",
                                            top: "10px",
                                            bottom: "10px",
                                            width: "2px",
                                            zIndex: 1,
                                        }}
                                    />

                                    {[...product.timeline]
                                        .sort((a, b) => timelineTime(b.time) - timelineTime(a.time))
                                        .map((item, index) => (
                                            <div key={index} className="position-relative ps-4 pb-4" style={{ zIndex: 2 }}>
                                                <div
                                                    className="position-absolute bg-primary rounded-circle border border-2 border-white shadow-sm"
                                                    style={{
                                                        left: "5px",
                                                        top: "4px",
                                                        width: "14px",
                                                        height: "14px",
                                                    }}
                                                />

                                                <div className="bg-light p-3 rounded-3 border">
                                                    <div className="fw-bold text-dark small">{item.title}</div>
                                                    <small className="text-muted d-block mb-1">{item.time}</small>

                                                    {item.movement_type && (
                                                        <div className="small text-primary fw-semibold mt-1">
                                                            {item.movement_type}
                                                        </div>
                                                    )}

                                                    {item.handover_image && (
                                                        <div className="mt-2 border-top pt-2">
                                                            <span className="d-block text-muted small mb-1">{t("Handover Proof")}:</span>
                                                            <span onClick={() => setModalImage(`${imgBaseUrl}${item.handover_image}`)} className="cursor-pointer d-inline-block">
                                                                <Image
                                                                    src={`${imgBaseUrl}${item.handover_image}`}
                                                                    alt="Handover Image"
                                                                    rounded
                                                                    className="border shadow-sm object-fit-cover"
                                                                    style={{
                                                                        width: "60px",
                                                                        height: "60px",
                                                                    }}
                                                                />
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted small">
                                    {t("No timeline history recorded.")}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={!!modalImage} onHide={() => setModalImage(null)} centered size="lg">
                <Modal.Body className="text-center p-0 bg-dark position-relative">
                    <Button
                        variant="light"
                        size="sm"
                        className="position-absolute top-0 end-0 m-3 rounded-circle"
                        onClick={() => setModalImage(null)}
                        style={{ zIndex: 10 }}
                    >
                        &times;
                    </Button>
                    <img
                        src={modalImage}
                        alt="Zoomed"
                        className="img-fluid max-h-80vh object-fit-contain"
                        style={{ maxHeight: "80vh", objectFit: "contain" }}
                    />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}